import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "@app/projects/literals";
import { AccuracyAggregateInspectionResultGroup } from "@app/projects/results/accuracy/accuracyAggregateInspectionResultGroup";
import { AccuracySummarySpecialtyGroup } from "@app/projects/results/accuracy/accuracySummarySpecialtyGroup";
import {
  AnalysisSpecialtyGroup,
  ResultRegion
} from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";

interface AccuracyDetail {
  key: string;
  defaultDisplayOrder: number;
  displayText: string;
  linkSuffix: string;

  providerCount: number;
  accuracyIndex: number;
  providerPassPercentage: number;
  providerFailPercentage: number;
  resultGroups: AccuracyAggregateInspectionResultGroup[];

  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionGranularity: RegionGranularity;
  subRegionCode: string;

  specialtyGroupCode: string;
  specialtyGroupDisplayName: string;
  specialtyGroupDisplayOrder: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  specialtyGroupIsInformational: boolean;
  specialtyGroupName?: string;

  recentlyAttestedCount?: number;
  recentlyAttestedPercent?: number;
  ninetyDayAttestedPercent?: number;
  ninetyDayAttestedCount?: number;

  parentAbbreviation?: string;
  parentDisplayName?: string;
}

class AccuracyDetailImpl implements AccuracyDetail {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public providerCount: number,
    public accuracyIndex: number,
    public providerPassPercentage: number,
    public providerFailPercentage: number,
    public resultGroups: AccuracyAggregateInspectionResultGroup[],

    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,

    public specialtyGroupCode: string,
    public specialtyGroupDisplayName: string,
    public specialtyGroupDisplayOrder: number,
    public specialtyGroupIsInformational: boolean,

    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupName?: string,

    public recentlyAttestedCount?: number,
    public recentlyAttestedPercent?: number,
    public ninetyDayAttestedPercent?: number,
    public ninetyDayAttestedCount? : number,

    public parentAbbreviation?: string,
    public parentDisplayName?: string,
  ) { }

  public get defaultDisplayOrder(): number {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetId;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${this.specialtyGroupCode}`;
      case "unknown":
        return null;
    }
  }
}

function buildAccuracyDetail(
  key: string,
  resultRegion: ResultRegion,
  summarySpecialtyGroup: AccuracySummarySpecialtyGroup,
  specialtyGroup: AnalysisSpecialtyGroup,
  exception: SpecialtyGroupException,
  aggregateDisplayType: AggregateDisplayType,
): AccuracyDetail {
  let newDetail: AccuracyDetail = {} as AccuracyDetail;

  if (!resultRegion || !summarySpecialtyGroup || !specialtyGroup) {
    return newDetail;
  }

  newDetail = new AccuracyDetailImpl(
    key,
    aggregateDisplayType,

    summarySpecialtyGroup.providerCount,
    summarySpecialtyGroup.accuracyIndex,
    summarySpecialtyGroup.providerPassPercentage,
    summarySpecialtyGroup.providerFailPercentage,
    summarySpecialtyGroup.resultGroups,

    resultRegion.regionCode,
    resultRegion.regionGranularity,
    resultRegion.displayName,
    resultRegion.regionSetId,
    resultRegion.subRegionCode,

    specialtyGroup.code,
    `${specialtyGroup.code} - ${specialtyGroup.name}`,
    specialtyGroup.displayOrder,
    specialtyGroup.isInformational,

    specialtyGroup.entityType,
    specialtyGroup.name,

    summarySpecialtyGroup.recentlyAttestedCount,
    summarySpecialtyGroup.recentlyAttestedPercent,
    summarySpecialtyGroup.ninetyDayAttestedPercent,
    summarySpecialtyGroup.ninetyDayAttestedPercent
  );

  return newDetail;
}

/**
 * Clones an `AccuracyDetail` while setting a new displayText property based on the `AggregateDisplayType`
 * It DOES actually create a new object
 */
function pseudoClone(
  detail: AccuracyDetail,
  aggregateDisplayType: AggregateDisplayType,
): AccuracyDetail {

  return new AccuracyDetailImpl(
    detail.key,
    aggregateDisplayType,

    detail.providerCount,
    detail.accuracyIndex,
    detail.providerPassPercentage,
    detail.providerFailPercentage,
    detail.resultGroups,

    detail.regionCode,
    detail.regionGranularity,
    detail.regionSetDisplayName,
    detail.regionSetId,
    detail.subRegionCode,

    detail.specialtyGroupCode,
    detail.specialtyGroupDisplayName,
    detail.specialtyGroupDisplayOrder,
    detail.specialtyGroupIsInformational,

    detail.specialtyGroupEntityType,
    detail.specialtyGroupName,

    detail.recentlyAttestedCount,
    detail.recentlyAttestedPercent,

    detail.ninetyDayAttestedCount,
    detail.ninetyDayAttestedPercent,

    detail.parentAbbreviation,
    detail.parentDisplayName,
  );
}

export { AccuracyDetail, buildAccuracyDetail, pseudoClone };
